import { useState } from 'react';
import { Button, Card, Col, Container, Row, Tab, Tabs } from 'react-bootstrap';
import { toast } from 'react-toastify';
import { Link, useSearchParams } from 'react-router-dom';
import HelpAssistant from './HelpAssistant';
import AccountManagerCompare from './AccountManagerCompare';
import AccountManagerGuide from './AccountManagerGuide';
import AccountManagerAffiliates from './AccountManagerAffiliates';
import AccountManagerOrganizations from './AccountManagerOrganizations';
import AccountManagerPlans from './AccountManagerPlans';
import AccountManagerTickets from './AccountManagerTickets';
import useSectionTab from '../hooks/useSectionTab';

/** Landing page for a logged-in Account Manager: the plans they can sell, their organizations, support tickets and sales guides. */
const ACCOUNT_MANAGEMENT_SECTIONS = ['plans', 'compare', 'organizations', 'tickets', 'affiliates', 'guides', 'assistant'];

/** A short message to go with the brochure in a WhatsApp group; the links are the public brochure and the sign-up page. */
const BROCHURE_MESSAGE = [
  'iBigDo: one system for purchase, stock, production, sales and accounts, built for manufacturing, processing and trading businesses in India.',
  'GST-ready, with quotations and payment reminders you can send on WhatsApp.',
  '',
  'Brochure: https://ibigdo.com/guide/ibigdo-brochure.pdf',
  'Try it free: https://ibigdo.com/register'
].join('\n');

// The clipboard API where the browser allows it, else the older way (a page that is not secure, or an older browser)
const copyBrochureMessage = async () => {
  let copied = false;
  try {
    await navigator.clipboard.writeText(BROCHURE_MESSAGE);
    copied = true;
  } catch (e) {
    const box = document.createElement('textarea');
    box.value = BROCHURE_MESSAGE;
    box.style.position = 'fixed';
    box.style.opacity = '0';
    document.body.appendChild(box);
    box.select();
    try { copied = document.execCommand('copy'); } catch (err) { copied = false; }
    document.body.removeChild(box);
  }
  if (copied) toast.success('Message copied. Paste it in WhatsApp with the brochure.');
  else toast.error('Could not copy; select the message and copy it by hand.');
};

/** The selling and onboarding guides, in the order an Account Manager uses them; served only to account managers. */
const GUIDES = [
  {
    name: 'discovery-questions',
    step: '1. Before the demo',
    title: 'Discovery questions',
    text: 'What to ask a prospect on the first calls, why each question helps, and how to turn the answers into a plan and a demo.'
  },
  {
    name: 'demo-playbook',
    step: '2. The demo',
    title: 'Demo playbook',
    text: 'Preparing the demo, what to show stage by stage, handling objections and closing with a date.'
  },
  {
    name: 'onboarding-guide',
    step: '3. After they sign',
    title: 'Onboarding guide',
    text: 'The data checklist, setup steps, trial run, go-live and the first 30 days, until the customer runs without you.'
  }
];

function AccountManagement() {
  // The open section is in the address (/account-management/<section>)
  const [activeTab, setActiveTab] = useSectionTab('/account-management', ACCOUNT_MANAGEMENT_SECTIONS);
  // An open guide is in the address too (/account-management/guides?g=onboarding-guide)
  const [searchParams, setSearchParams] = useSearchParams();
  const openGuide = GUIDES.find((g) => g.name === searchParams.get('g'));

  return (
    <Container fluid className="mt-4">
      <Tabs activeKey={activeTab} onSelect={(k) => setActiveTab(k)} className="mb-3">
        <Tab
          eventKey="plans"
          title="Plans &amp; Pricing"
        >
          <AccountManagerPlans />
        </Tab>
        <Tab
          eventKey="compare"
          title="Compare"
        >
          <AccountManagerCompare />
        </Tab>
        <Tab
          eventKey="organizations"
          title="Organizations"
        >
          <AccountManagerOrganizations />
        </Tab>
        <Tab
          eventKey="tickets"
          title="Support Tickets"
        >
          <AccountManagerTickets />
        </Tab>
        <Tab
          eventKey="affiliates"
          title="Affiliates"
        >
          <AccountManagerAffiliates />
        </Tab>
        <Tab
          eventKey="guides"
          title="Sales Guides"
        >
          {openGuide ? (
            <AccountManagerGuide name={openGuide.name} onBack={() => setSearchParams({})} />
          ) : (
            <>
              <p className="text-muted">Guides for selling iBigDo and onboarding customers, for account managers only. Each can be printed.</p>
              <Row className="g-3">
                {GUIDES.map((g) => (
                  <Col md={4} key={g.name}>
                    <Card className="h-100">
                      <Card.Body>
                        <div className="small text-muted text-uppercase mb-1">{g.step}</div>
                        <Card.Title as="h6">
                          <Link to={`/account-management/guides?g=${g.name}`}>{g.title}</Link>
                        </Card.Title>
                        <Card.Text className="small">{g.text}</Card.Text>
                      </Card.Body>
                    </Card>
                  </Col>
                ))}
              </Row>
              <Card className="mt-3">
                <Card.Body>
                  <div className="small text-muted text-uppercase mb-1">To share</div>
                  <Card.Title as="h6">iBigDo brochure (PDF)</Card.Title>
                  <Card.Text className="small">
                    Two pages for prospects, small enough for WhatsApp groups. It is public, so the link works for anyone, and it leaves out prices:
                    send them to you or to the sign-up page for a plan.
                  </Card.Text>
                  <div className="d-flex flex-wrap gap-2 mb-2">
                    <Button as="a" size="sm" href="/guide/ibigdo-brochure.pdf" target="_blank" rel="noopener noreferrer" download="iBigDo-brochure.pdf">
                      <i className="bi bi-file-earmark-pdf me-1" />Download the brochure
                    </Button>
                    <Button size="sm" variant="outline-secondary" onClick={copyBrochureMessage}>
                      <i className="bi bi-clipboard me-1" />Copy the WhatsApp message
                    </Button>
                  </div>
                  <pre className="small text-muted mb-0" style={{ whiteSpace: 'pre-wrap', fontFamily: 'inherit' }}>{BROCHURE_MESSAGE}</pre>
                </Card.Body>
              </Card>
            </>
          )}
        </Tab>
        <Tab
          eventKey="assistant"
          title="Search &amp; Ask"
        >
          <HelpAssistant />
        </Tab>
      </Tabs>

    </Container>
  );
}

export default AccountManagement;
